import React from 'react';
import { toast } from 'sonner';
import { z } from 'zod/v3';
import { trpc, getAuthHeaders } from './trpc';
import { inferProcedureOutput } from '@trpc/server';
import { useMutation } from '@tanstack/react-query';
import { Buffer } from 'buffer';
import { getImageDimensions, getVideoDimensions } from '@/utils/image';
import { apiEndpoints } from './env';

export type UploadedFile = inferProcedureOutput<typeof trpc.file.uploadImage>;

interface UseUploadFileProps {
  fileProps?: {
    category?: 'avatar' | 'article' | 'source' | 'other' | 'logo' | 'favicon' | 'meta';
  }
  onUploadComplete?: (file: UploadedFile) => void;
  onUploadError?: (error: unknown) => void;
}

export function useUploadFile({
  fileProps,
  onUploadComplete,
  onUploadError,
}: UseUploadFileProps = {}) {
  const [uploadedFile, setUploadedFile] = React.useState<UploadedFile>();
  const [uploadingFile, setUploadingFile] = React.useState<File>();
  const [progress, setProgress] = React.useState<number>(0);
  const [isUploading, setIsUploading] = React.useState(false);

  const uploadMutation = useMutation({
    mutationFn: async (data: { filename: string; contentType: string; content: string; size: number; width?: number; height?: number; length?: number; category?: 'avatar' | 'article' | 'source' | 'other' | 'logo' | 'favicon' | 'meta' }) => {
      const headers = await getAuthHeaders();
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            console.log('uploadMutation.progress', percentComplete);
            setProgress(Math.min(percentComplete, 100));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (error: unknown) {
              reject(error);
            }
          } else {
            reject(new Error('Upload failed'));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error'));
        });

        xhr.open('POST', `${apiEndpoints.trpcApi}/file.upload`);

        for (const [key, value] of Object.entries(headers)) {
          xhr.setRequestHeader(key, value);
        }
        xhr.send(JSON.stringify({ json: data }));
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (result: any) => {
      // console.log('uploadMutation.onSuccess', result);
      const uploadedFile = result?.result?.data?.json as UploadedFile;
      console.log('uploadMutation.onSuccess', uploadedFile);
      setUploadedFile(uploadedFile);
      onUploadComplete?.(uploadedFile);
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      const message = errorMessage.length > 0
        ? errorMessage
        : 'Something went wrong, please try again later.';

      toast.error(message);
      onUploadError?.(error);
    },
  });

  async function uploadFile(file: File) {
    setIsUploading(true);
    setUploadingFile(file);

    try {
      const content = await file.arrayBuffer();
      const base64Content = Buffer.from(content).toString('base64');

      let dimensions: { width: number; height: number; length: number } = { width: 0, height: 0, length: 0 };
      if (file.type.includes('image')) {
        dimensions = await getImageDimensions(`data:${file.type};base64,${base64Content}`) as { width: number; height: number; length: number };
      } else if (file.type.includes('video')) {
        // get video duration
        dimensions = await getVideoDimensions(file);
      }

      // console.log('dimensions', dimensions);
      const result = await uploadMutation.mutateAsync({
        filename: file.name,
        contentType: file.type,
        content: base64Content,
        size: file.size,
        category: fileProps?.category,
        width: dimensions.width,
        height: dimensions.height,
        length: dimensions.length,
      });

      return result;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      const message = errorMessage.length > 0
        ? errorMessage
        : 'Something went wrong, please try again later.';

      toast.error(message);
      onUploadError?.(error);
    } finally {
      setProgress(0);
      setIsUploading(false);
      setUploadingFile(undefined);
    }
  }

  return {
    isUploading,
    progress,
    uploadedFile,
    uploadFile: uploadFile,
    uploadingFile,
  };
}

function getErrorMessage(err: unknown) {
  const unknownError = 'Something went wrong, please try again later.';

  if (err instanceof z.ZodError) {
    const errors = err.issues.map((issue) => {
      return issue.message;
    });

    return errors.join('\n');
  } else if (err instanceof Error) {
    return err.message;
  } else {
    return unknownError;
  }
}
